import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import NavMarker from 'react-rhumb/NavMarker';
import { useLocation, useParams, useRouteMatch, useHistory } from 'react-router-dom';
import { parse as parseParams } from 'hub-http/helpers/params';
import { GYPSUM, OLAF } from 'HubStyleTokens/colors';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
// @ts-expect-error untyped dependency
import localeComparator from 'I18n/utils/localeComparator';
import unescapedText from 'I18n/utils/unescapedText';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIButton from 'UIComponents/button/UIButton';
import Loadable from 'UIComponents/decorators/Loadable';
import HR from 'UIComponents/elements/HR';
import Small from 'UIComponents/elements/Small';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UILink from 'UIComponents/link/UILink';
import UIDescriptionList from 'UIComponents/list/UIDescriptionList';
import UIListingPage from 'UIComponents/page/UIListingPage';
import UISection from 'UIComponents/section/UISection';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { DetailMainSection as MainSection } from 'marketplace-ui-common/layout/components/MainSection';
import { MainSectionHeading } from 'marketplace-ui-common/layout/components/MainSectionHeading';
import { Requirements } from 'marketplace-ui-apps-core/details/components/sidebar/Requirements';
import { useAppInstallability } from 'marketplace-ui-apps-core/installability/hooks/useAppInstallability';
import { useHeaderTabs } from 'marketplace-ui-common/hooks/useHeaderTabs';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useScrollEventListener } from 'marketplace-ui-common/hooks/useScrollEventListener';
import { useScrollToTop } from 'marketplace-ui-common/hooks/useScrollToTop';
import { useUsageTracker } from 'marketplace-ui-tracking';
import { useWindowDimensionsContext } from 'marketplace-ui-common/hooks/useWindowDimensionsContext';
import DocumentHead from 'marketplace-ui-common/components/portals/DocumentHead';
import InnerSection from 'marketplace-ui-common/layout/components/InnerSection';
import InnerSectionHeading from 'marketplace-ui-common/layout/components/InnerSectionHeading';
import MainSectionInner from 'marketplace-ui-common/layout/components/MainSectionInner';
import MediaCarousel from 'marketplace-ui-apps-core/details/components/MediaCarousel';
import MetaOpenGraph from 'marketplace-ui-common/components/elements/MetaOpenGraph';
import MetaTwitter from 'marketplace-ui-common/components/elements/MetaTwitter';
import PrerenderReady from 'marketplace-ui-common/components/prerender/PrerenderReady';
import SharedData from 'marketplace-ui-apps-core/details/components/SharedData';
import * as FilterKey from '../constants/search/FilterKey';
import { AppDetails } from '../components/detail/overview/sidebar/AppDetails';
import { FeatureCompatibilityModal } from '../components/detail/overview/sidebar/FeatureCompatibilityModal';
import { FeatureCards } from '../components/detail/overview/FeatureCards';
import { LegacyFeatureCompatibilityModal } from '../components/detail/overview/sidebar/LegacyFeatureCompatibilityModal';
import { IntegrationFeatures } from '../components/detail/overview/IntegrationFeatures';
import { PublicOnly, getHasZorseChatScope, getHasZorseWebticketScope, getIsCustomer, getIsPublic, getIsUngatedForRecategorization
// @ts-expect-error untyped dependency
} from '../selectors/auth';
import { getAllReviewsUrl, getFormattedUrl, getListingDetailUrl } from '../utils/urls';
import { getFullHref } from '../utils/history';
import { NAV_BAR_HEIGHT_INT } from '../constants';
// @ts-expect-error untyped dependency
import { openHelpWidget } from '../utils/zorse';
import { useAppContext } from '../context/AppContext';
import { useAppRelatedInstallStats } from '../utils/hooks/useAppRelatedInstallStats';
import { useListingView } from '../utils/hooks/useListingView';
// @ts-expect-error untyped dependency
import { useParsedPathParams } from '../utils/hooks/useParsedPathParams';
import { useSocialScreenshotUrl } from '../utils/hooks/useSocialScreenshot';
import { AppRelatedInstallStatsSection } from '../components/storefront/sidebar/AppRelatedInstallStatsSection';
// @ts-expect-error untyped dependency
import AppIntegrations from '../components/detail/overview/AppIntegrations';
// @ts-expect-error untyped dependency
import Benefits from '../components/detail/overview/Benefits';
// @ts-expect-error untyped dependency
import CanonicalLink from '../components/elements/CanonicalLink';
// @ts-expect-error untyped dependency
import Contact from '../components/detail/overview/sidebar/Contact';
// @ts-expect-error untyped dependency
import Header, { TAB_IDS } from '../components/detail/Header';
// @ts-expect-error untyped dependency
import HubSpotFeatures from '../components/detail/overview/HubSpotFeatures';
import MoreApps from '../components/detail/MoreApps';
// @ts-expect-error untyped dependency
import Privacy from '../components/detail/overview/sidebar/Privacy';
// @ts-expect-error untyped dependency
import Resources from '../components/detail/overview/sidebar/Resources';
// @ts-expect-error untyped dependency
import Pricings from '../components/detail/pricing/Pricings';
import Reviews from '../components/detail/reviews/Reviews';
// @ts-expect-error untyped dependency
import StorefrontFilters from '../records/StorefrontFilters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const {
  CATEGORY,
  SUPER_CATEGORY
} = FilterKey;
const AsyncCtaBanner = Loadable({
  loader: () => import(
  // @ts-expect-error untyped dependencys
  /* webpackChunkName: 'detail-cta-banner' */
  './CtaBanner')
});
const SidebarSection = props => /*#__PURE__*/_jsx(InnerSection, Object.assign({}, props, {
  compact: true
}));
const Section = ({
  children,
  label
}) => /*#__PURE__*/_jsxs(Fragment, {
  children: [/*#__PURE__*/_jsx("dt", {
    children: label
  }), /*#__PURE__*/_jsx("dd", {
    children: children
  })]
});
export function AppDetailsPage({
  isAdminPreview = false
}) {
  const isUngatedForAppRecategorization = useSelector(getIsUngatedForRecategorization);
  const appContext = useAppContext();
  const {
    pathname,
    search
  } = useLocation();
  const params = useParams();
  const {
    path,
    url
  } = useRouteMatch();
  const {
    height: windowHeight
  } = useWindowDimensionsContext();
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  useScrollToTop(pathname);
  const {
    category: activeCategory,
    superCategory: activeSuperCategory,
    slug
  } = useParsedPathParams(params);
  const hasZorseChatScope = useSelector(getHasZorseChatScope);
  const hasZorseWebticketScope = useSelector(getHasZorseWebticketScope);
  const isCustomer = useSelector(getIsCustomer);
  const location = useLocation();
  const history = useHistory();
  const referrer = parseParams(location.search.slice(1)).ref;
  const {
    listingView
  } = useListingView();
  const {
    published,
    source,
    language: languageQuery,
    embeddedFeatureCompatibility
  } = parseParams(search.slice(1));
  const langCode = getLang();
  const isPreview = published === 'false' || path === '/_preview/:offeringId' || isAdminPreview;
  const isPublic = useSelector(getIsPublic);
  const isReviewLink = path.includes('/:slug/write-review');
  const tracker = useUsageTracker();
  const [pageViewTracked, setPageViewTracked] = useState(false);
  const listingCategory = useMemo(() => {
    return activeCategory || listingView.category || null;
  }, [activeCategory, listingView]);
  const listingSuperCategory = useMemo(() => {
    return activeSuperCategory || listingView.getSuperCategory() || null;
  }, [activeSuperCategory, listingView]);
  const scrollOffset = NAV_BAR_HEIGHT_INT + windowHeight / 2;
  const {
    calculateActiveTab,
    scrollToTabSection,
    activeTab
  } = useHeaderTabs(Object.values(TAB_IDS), scrollOffset);
  useScrollEventListener(calculateActiveTab);
  const {
    agencyPricingUrl,
    appIntegrations,
    appLanguages,
    appUrl,
    benefits,
    casesUrl,
    categoryFeatures,
    categoryV2,
    chatUrl,
    companyName,
    companyUrl,
    currency,
    dataSyncHowItWorks,
    dataSyncInfo,
    dataSyncs,
    email,
    facebookUrl,
    features,
    forumUrl,
    hubSpotFeatures,
    iconUrl,
    id,
    integrationFeatures,
    keywords,
    name,
    neededScopeGroups,
    offering: {
      credentials,
      connectionType,
      homemade,
      installsCount,
      portalId: offeringPortalId
    },
    offeringId,
    opsHub,
    phone,
    pricingUrl,
    pricings,
    privacy2Url,
    privacy3Url,
    privacyUrl,
    screenshots,
    setupUrl,
    supportLanguages,
    supportUrl,
    tagline,
    termsUrl,
    tiers,
    updatedAt,
    videoUrl
  } = listingView;
  const {
    installability
  } = useAppInstallability({
    appContext,
    offeringId: offeringId !== null && offeringId !== void 0 ? offeringId : 0
  });
  const appId = listingView.getAppId();
  const detailsClassName = isMobileView ? 'p-top-6' : "";
  const overviewClassName = !isMobileView ? 'p-right-15' : "";
  const [featureCompatibilityModalOpen, setFeatureCompatibilityModalOpen] = useState(!isPublic && embeddedFeatureCompatibility === 'true');
  const documentTitle = useMemo(() => {
    return unescapedText('appMarketplace.detail.htmlTitle', {
      name
    });
  }, [name]);
  const preferredLanguage = useMemo(() => {
    return isPreview ? languageQuery || 'en' : langCode;
  }, [langCode, languageQuery, isPreview]);
  const eventProperties = useMemo(() => {
    let screen;
    if (isPreview) {
      screen = 'detail preview';
    } else if (isReviewLink) {
      screen = 'detail review';
    } else {
      screen = 'detail';
    }
    return Object.assign({}, listingView.getEventProperties(isUngatedForAppRecategorization), {
      slug,
      screen,
      preferredLanguage
    });
  }, [isPreview, isReviewLink, listingView, isUngatedForAppRecategorization, slug, preferredLanguage]);
  const trackEvent = useCallback((eventName, properties = {}) => {
    tracker === null || tracker === void 0 || tracker.track(eventName, Object.assign({}, properties, eventProperties));
  }, [eventProperties, tracker]);
  const trackConnectButtonClick = useCallback(() => {
    trackEvent('listing-detail-click-connect');
  }, [trackEvent]);
  const trackInteraction = useCallback((action, properties = {}) => {
    trackEvent('listing-detail-interaction', Object.assign({
      action
    }, properties));
  }, [trackEvent]);

  // TODO: remove after conv-0002 experiment
  // this is only here for the sake of the experiment-------------------
  const appRelatedInstallStats = useAppRelatedInstallStats(offeringId);
  const hasAppRelatedInstallStats = appRelatedInstallStats.country || appRelatedInstallStats.industry || appRelatedInstallStats.plan;
  const getAppRelatedInstallStatValue = useCallback(stat => {
    return appRelatedInstallStats[stat] && Object.values(appRelatedInstallStats[stat])[0];
  }, [appRelatedInstallStats]);
  // ----------------------------------------------------------------------
  const trackPageview = useCallback((properties = {}) => {
    let eventName;
    if (isPreview) {
      eventName = 'listing-detail-preview-pageview';
    } else if (isReviewLink) {
      eventName = 'listing-detail-review-pageview';
    } else {
      eventName = 'listing-detail-pageview';
    }
    trackEvent(eventName, Object.assign({}, properties, {
      showAppRelatedInstallStats: !!hasAppRelatedInstallStats,
      appRelatedIndustryStat: getAppRelatedInstallStatValue('industry'),
      appRelatedCountryStat: getAppRelatedInstallStatValue('country'),
      appRelatedPlanStat: getAppRelatedInstallStatValue('plan'),
      referrer: referrer || undefined,
      preferredLanguage,
      source
    }));
  }, [isPreview, isReviewLink, trackEvent, hasAppRelatedInstallStats, getAppRelatedInstallStatValue, referrer, preferredLanguage, source]);
  const pricingLastUpdated = useMemo(() => {
    if (pricings[0] && pricings[0].updatedAt) {
      return pricings[0].updatedAt;
    }
    return updatedAt;
  }, [pricings, updatedAt]);
  const getFilterLink = useCallback((filterKey, tagName, isAllCategories = false) => {
    let filters = StorefrontFilters.from(Object.assign({}, filterKey && {
      [filterKey]: [tagName]
    }, {
      [CATEGORY]: listingCategory,
      [SUPER_CATEGORY]: listingSuperCategory
    }));
    if (isAllCategories || isUngatedForAppRecategorization) {
      filters = filters.toAllCategories();
    }
    return filters.toLocation();
  }, [isUngatedForAppRecategorization, listingCategory, listingSuperCategory]);
  useEffect(() => {
    document.title = documentTitle;
    if (!pageViewTracked && !appRelatedInstallStats.installStatsLoading) {
      trackPageview();
      setPageViewTracked(true);
    }
  }, [appRelatedInstallStats.installStatsLoading, documentTitle, pageViewTracked, trackPageview]);

  // Reset the ref query param to avoid tracking the wrong referrer when the user refreshes
  useEffect(() => {
    if (referrer) {
      history.replace({
        search: ''
      });
    }
  }, [history, referrer]);
  const mediaCarouselItems = useMemo(() => {
    const carouselItems = screenshots.map(s => ({
      type: 'image',
      source: s.imageUrl,
      alt: s.altText ? s.altText : ''
    }));
    if (videoUrl) {
      carouselItems.unshift({
        type: 'video',
        source: videoUrl,
        alt: ''
      });
    }
    return carouselItems;
  }, [screenshots, videoUrl]);
  const renderedResourcesSection = useMemo(() => /*#__PURE__*/_jsxs(SidebarSection, {
    "data-test-id": "details-resources",
    children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.detail.overview.sidebar.resources.headingText"
      })
    }), /*#__PURE__*/_jsx(Resources, {
      casesUrl: casesUrl,
      forumUrl: forumUrl,
      onTrackInteraction: trackInteraction,
      setupUrl: setupUrl,
      companyUrl: companyUrl
    })]
  }), [casesUrl, companyUrl, forumUrl, trackInteraction, setupUrl]);
  const renderedSupportLanguagesList = useMemo(() => /*#__PURE__*/_jsx(FormattedList, {
    children: supportLanguages.map(languageCode => [languageCode, `SharedI18nStrings.languageNames.${languageCode}`]).sort(([__, languageANameI18nKey], [___, languageBNameI18nKey]) => localeComparator(unescapedText(languageANameI18nKey), unescapedText(languageBNameI18nKey))).map(([languageCode, languageNameI18nKey]) => /*#__PURE__*/_jsx(FormattedMessage, {
      message: languageNameI18nKey
    }, languageCode))
  }), [supportLanguages]);
  const renderedSupportSection = useMemo(() => /*#__PURE__*/_jsxs(SidebarSection, {
    "data-test-id": "details-support",
    children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
      children: homemade ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.detail.overview.sidebar.support.homemadeHeadingText",
        options: {
          name
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.detail.overview.sidebar.support.headingText",
        options: {
          companyName
        }
      })
    }), /*#__PURE__*/_jsx(Contact, {
      chatUrl: chatUrl,
      supportUrl: supportUrl,
      email: email,
      facebookUrl: facebookUrl,
      hasZorseChatScope: hasZorseChatScope,
      hasZorseWebticketScope: hasZorseWebticketScope,
      homemade: homemade,
      isCustomer: isCustomer,
      onTrackInteraction: trackInteraction,
      openHelpWidget: openHelpWidget,
      phone: phone
    }), /*#__PURE__*/_jsx(UIDescriptionList, {
      className: "p-top-2",
      children: /*#__PURE__*/_jsx(Section, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.overview.sidebar.support.supportedLanguages"
        }),
        children: renderedSupportLanguagesList
      })
    })]
  }), [chatUrl, companyName, email, facebookUrl, hasZorseChatScope, hasZorseWebticketScope, homemade, isCustomer, renderedSupportLanguagesList, supportUrl, trackInteraction, phone, name]);
  const renderedPrivacySection = useMemo(() => /*#__PURE__*/_jsxs(SidebarSection, {
    "data-test-id": "details-privacy",
    children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.detail.overview.sidebar.privacy.headingText"
      })
    }), /*#__PURE__*/_jsx(Privacy, {
      homemade: homemade,
      onTrackInteraction: trackInteraction,
      privacyUrl: privacyUrl,
      privacy2Url: privacy2Url,
      privacy3Url: privacy3Url,
      termsUrl: termsUrl
    })]
  }), [homemade, trackInteraction, privacyUrl, privacy2Url, privacy3Url, termsUrl]);
  const renderedReportLink = useMemo(() => {
    return isCustomer && hasZorseWebticketScope ? /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(HR, {
        distance: "medium"
      }), /*#__PURE__*/_jsx(SidebarSection, {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "appMarketplace.detail.overview.sidebar.reportApp_jsx",
          options: {
            onClick: () => {
              trackInteraction('click report this app link', {
                section: 'report'
              });
              openHelpWidget({
                create: true
              });
            }
          },
          elements: {
            Link: UILink
          }
        })
      })]
    }) : /*#__PURE__*/_jsx(Fragment, {});
  }, [isCustomer, hasZorseWebticketScope, trackInteraction]);
  const socialImageUrl = useSocialScreenshotUrl();
  const renderedFeatureCompatibilityModal = useMemo(() => {
    const inAppOrPublicContext = isCustomer ? 'app' : 'public';
    const onClose = () => {
      setFeatureCompatibilityModalOpen(false);
      // remove the query param from the URL so
      // the modal doesn't open again on refresh
      if (embeddedFeatureCompatibility === 'true') {
        const urlObj = new URL(window.location.href);
        urlObj.searchParams.delete('embeddedFeatureCompatibility');
        window.history.replaceState({}, '', urlObj);
      }
    };
    const tierGroupNames = ['CRM', 'OPERATIONS', 'MARKETING', 'SALES', 'SERVICE', 'CONTENT'];
    if (features !== null && features !== void 0 && features.length) {
      return /*#__PURE__*/_jsx(FeatureCompatibilityModal, {
        appContext: inAppOrPublicContext,
        features: features,
        installability: installability,
        onClose: onClose,
        tierGroupNames: tierGroupNames,
        trackInteraction: trackInteraction
      });
    } else {
      // If there are no feature editor defined features,
      // we'll call useCompatibility to build out the modal
      // with the deprecated /v1/compatibility/opsHub endpoint
      return /*#__PURE__*/_jsx(LegacyFeatureCompatibilityModal, {
        appContext: inAppOrPublicContext,
        integrationFeatures: integrationFeatures,
        onClose: onClose,
        tierGroupNames: tierGroupNames,
        trackInteraction: trackInteraction
      });
    }
  }, [isCustomer, features, installability, integrationFeatures, trackInteraction, embeddedFeatureCompatibility, setFeatureCompatibilityModalOpen]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [isPreview && !isAdminPreview && /*#__PURE__*/_jsx(UIAlert, {
      titleText: unescapedText('appMarketplace.detail.previewAlert.titleText'),
      type: "warning",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.detail.previewAlert.message"
      })
    }), /*#__PURE__*/_jsxs(UIListingPage, {
      contentAreaFlush: true,
      headerComponent: /*#__PURE__*/_jsx(NavMarker, {
        name: "DETAIL_HEADER_LOAD",
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsx(Header, {
            activeTab: activeTab,
            appId: appId,
            appName: name,
            appUrl: appUrl,
            credentials: credentials,
            connectionType: connectionType,
            iconUrl: iconUrl,
            isCustomer: isCustomer,
            isPreview: isPreview,
            isDataSyncApp: opsHub,
            listingId: id,
            offeringId: offeringId,
            onConnectButtonClick: trackConnectButtonClick,
            onTrackEvent: trackEvent,
            onTrackInteraction: trackInteraction,
            onTrackPageview: trackPageview,
            scrollToTabSection: scrollToTabSection,
            setupUrl: setupUrl,
            tagline: tagline
          })
        })
      }),
      mainSectionBackgroundColor: OLAF,
      pageBackgroundColor: GYPSUM,
      pageLayout: "full-width",
      children: [/*#__PURE__*/_jsx(MainSection, {
        id: "overview",
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsxs(UIGrid, {
            children: [/*#__PURE__*/_jsxs(UIGridItem, {
              className: overviewClassName,
              size: {
                xs: 12,
                md: 8
              },
              children: [/*#__PURE__*/_jsx(InnerSection, {
                children: /*#__PURE__*/_jsx(MediaCarousel, {
                  "data-test-id": "carousel",
                  items: mediaCarouselItems,
                  onTrackInteraction: action => trackInteraction(action, {
                    section: 'overview'
                  })
                })
              }), /*#__PURE__*/_jsxs(InnerSection, {
                children: [/*#__PURE__*/_jsx(MainSectionHeading, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.headingText",
                    options: {
                      name
                    }
                  })
                }), /*#__PURE__*/_jsx(Benefits, {
                  benefits: benefits
                })]
              }), /*#__PURE__*/_jsxs(InnerSection, {
                id: "features",
                "data-test-id": "details-integrations",
                children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
                  className: "m-bottom-6",
                  itemRight: features !== null && features !== void 0 && features.length && installability || opsHub ? /*#__PURE__*/_jsx(UIButton, {
                    use: "link",
                    onClick: () => {
                      setFeatureCompatibilityModalOpen(true);
                      trackInteraction('click compare features by plan', {
                        section: 'overview'
                      });
                    },
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "appMarketplace.detail.overview.integrationFeatures.compatibilityLink"
                    })
                  }) : null,
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.integrationFeatures.headingText",
                    options: {
                      name
                    }
                  })
                }), features !== null && features !== void 0 && features.length ? /*#__PURE__*/_jsx(FeatureCards, {
                  features: features,
                  installability: installability,
                  onTrackInteraction: trackInteraction
                }) : /*#__PURE__*/_jsx(IntegrationFeatures, {
                  integrationFeatures: integrationFeatures,
                  isOpsHubListing: opsHub,
                  onTrackInteraction: trackInteraction
                })]
              }), /*#__PURE__*/_jsxs(InnerSection, {
                id: "sharedData",
                children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
                  className: "m-bottom-2",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.sharedData.headingText"
                  })
                }), /*#__PURE__*/_jsx(SharedData, {
                  dataSyncHowItWorks: dataSyncHowItWorks,
                  dataSyncInfo: dataSyncInfo,
                  dataSyncs: dataSyncs,
                  iconUrl: iconUrl,
                  name: name,
                  onTrackInteraction: trackInteraction,
                  opsHub: opsHub
                })]
              }), /*#__PURE__*/_jsxs(InnerSection, {
                children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.hubspotFeatures.headingText",
                    options: {
                      name
                    }
                  })
                }), /*#__PURE__*/_jsx(HubSpotFeatures, {
                  hubspotFeatures: hubSpotFeatures,
                  getFilterLink: getFilterLink,
                  isPreview: isPreview
                })]
              }), !!appIntegrations.length && /*#__PURE__*/_jsxs(InnerSection, {
                children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.appIntegrations.headingText",
                    options: {
                      name
                    }
                  })
                }), /*#__PURE__*/_jsx(AppIntegrations, {
                  integrations: appIntegrations
                })]
              })]
            }), /*#__PURE__*/_jsxs(UIGridItem, {
              className: detailsClassName,
              size: {
                xs: 12,
                md: 4
              },
              children: [appContext === 'app' && /*#__PURE__*/_jsx(AppRelatedInstallStatsSection, {
                offeringId: offeringId
              }), /*#__PURE__*/_jsxs(SidebarSection, {
                children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.sidebar.details.headingText"
                  })
                }), /*#__PURE__*/_jsx(AppDetails, {
                  appLanguages: appLanguages,
                  category: listingCategory,
                  categoryV2: categoryV2,
                  categoryFeatures: categoryFeatures,
                  companyName: companyName,
                  companyUrl: companyUrl,
                  getFilterLink: getFilterLink,
                  homemade: homemade,
                  installs: installsCount,
                  isPreview: isPreview,
                  onTrackInteraction: trackInteraction
                })]
              }), /*#__PURE__*/_jsxs(SidebarSection, {
                children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.sidebar.requirements.headingText"
                  })
                }), /*#__PURE__*/_jsx(Requirements, {
                  appContext: appContext,
                  appName: name,
                  companyName: companyName,
                  connectionType: connectionType,
                  homemade: homemade,
                  iconUrl: iconUrl,
                  installability: installability,
                  features: features,
                  integrationFeatures: integrationFeatures,
                  isOpsHubListing: opsHub,
                  neededScopeGroups: neededScopeGroups,
                  onOpenFeatureCompatibilityModal: () => setFeatureCompatibilityModalOpen(true),
                  onTrackInteraction: trackInteraction,
                  pricingUrl: pricingUrl,
                  pricings: pricings,
                  scrollToTabSection: scrollToTabSection,
                  tabIds: TAB_IDS,
                  tiers: tiers
                })]
              }), !isMobileView && /*#__PURE__*/_jsxs(Fragment, {
                children: [renderedResourcesSection, renderedSupportSection, renderedPrivacySection, renderedReportLink]
              })]
            })]
          })
        })
      }), /*#__PURE__*/_jsx(MainSection, {
        color: "light",
        id: "pricing",
        children: /*#__PURE__*/_jsxs(MainSectionInner, {
          children: [/*#__PURE__*/_jsx(MainSectionHeading, {
            itemRight: /*#__PURE__*/_jsxs(Fragment, {
              children: [agencyPricingUrl && /*#__PURE__*/_jsx(UIButton, {
                external: true,
                href: getFormattedUrl(agencyPricingUrl),
                onClick: () => trackInteraction('click agency pricing link', {
                  section: 'pricing'
                }),
                use: "link",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "appMarketplace.detail.pricing.agencyPricingLinkText"
                })
              }), pricingUrl && /*#__PURE__*/_jsx(UIButton, {
                external: true,
                href: getFormattedUrl(pricingUrl),
                onClick: () => trackInteraction('click software pricing link', {
                  section: 'pricing'
                }),
                use: "link",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "appMarketplace.detail.pricing.pricingLinkText"
                })
              })]
            }),
            subheading: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "appMarketplace.detail.pricing.subheadingText",
              options: {
                name
              }
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "appMarketplace.detail.pricing.headingText",
              options: {
                name
              }
            })
          }), /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(Pricings, {
              currency: currency,
              onTrackInteraction: trackInteraction,
              pricings: pricings
            })
          }), /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(Small, {
              children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                message: "appMarketplace.detail.pricing.disclaimerText_jsx",
                elements: {
                  b: 'b',
                  Date: FormattedDateTime
                },
                options: {
                  format: 'L',
                  type: isCustomer ? 'portalTz' : 'utc',
                  value: pricingLastUpdated
                }
              })
            })
          })]
        })
      }), isMobileView && /*#__PURE__*/_jsx(MainSection, {
        color: "light",
        children: /*#__PURE__*/_jsxs(MainSectionInner, {
          children: [renderedResourcesSection, renderedSupportSection, renderedPrivacySection, renderedReportLink]
        })
      }), /*#__PURE__*/_jsx(MainSection, {
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          id: "reviews",
          children: /*#__PURE__*/_jsx(UISection, {
            children: /*#__PURE__*/_jsx(Reviews, {
              allReviewsHref: getAllReviewsUrl(isPublic, slug),
              appName: name,
              appIcon: iconUrl,
              isPreview: isPreview,
              isReviewLink: isReviewLink,
              listingDetailUrl: getListingDetailUrl(slug),
              offeringId: offeringId,
              offeringPortalId: offeringPortalId || 0,
              slug: slug,
              supportLink: companyUrl,
              trackInteraction: trackInteraction
            })
          })
        })
      }), !isCustomer && /*#__PURE__*/_jsx(MainSection, {
        color: "dark",
        children: /*#__PURE__*/_jsx(InnerSection, {
          children: /*#__PURE__*/_jsx(AsyncCtaBanner, {
            appName: name,
            appLink: pricingUrl,
            companyName: companyName,
            companyUrl: companyUrl,
            homemade: homemade,
            pathname: pathname,
            onTrackInteraction: trackInteraction
          })
        })
      }), !isUngatedForAppRecategorization && /*#__PURE__*/_jsx(MoreApps, {
        category: listingCategory,
        superCategory: listingSuperCategory,
        slug: slug,
        trackInteraction: trackInteraction
      }), featureCompatibilityModalOpen && renderedFeatureCompatibilityModal]
    }), /*#__PURE__*/_jsx(PublicOnly, {
      children: /*#__PURE__*/_jsxs(DocumentHead, {
        children: [/*#__PURE__*/_jsx(CanonicalLink, {
          to: url
        }), /*#__PURE__*/_jsx("meta", {
          name: "description",
          content: tagline
        }), /*#__PURE__*/_jsx("meta", {
          name: "keywords",
          content: keywords.join(',')
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "card"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "site"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "type"
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "title",
          content: documentTitle
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "url",
          content: getFullHref(url)
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "description",
          content: tagline
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(PrerenderReady, {})]
      })
    })]
  });
}